import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Box from '@amzn/meridian/box';
import { DEFAULT_SHIFT_HALF_OPTIONS, ROUTES } from '../../../utils/constants';
import AppModal from '../../../components/AppModal/AppModal';
import RatesRosterLoader from '../../../components/RatesRosterLoader/RatesRosterLoader';
import { useAppDispatch } from '../../../store';
import {
  setHistoricalPlanData,
  setStationAssignmentData,
} from '../../../store/slices/stationAssignmentSlice';
import { getClockWindowNameBasedOnSelectedOption } from '../../../services/shiftPlanService';
import { getStationAssignments } from '../assignments/api';
import { GetStationAssignmentsResponse } from '../assignments/models';
import { getTodayDate } from '../models';
import PublishedAssignmentHeader from './PublishedAssignmentHeader';
import PublishedAssignmentScreen, {
  PublishedAssignmentView,
} from './PublishedAssignmentScreen';
import './index.scss';

/**
 * Smart container for the Published Assignment Screen.
 *
 * Owns routing parameter extraction, the data-fetch effect, and the four
 * discrete render states (loading / missing-plan-id / generic-error /
 * success). Mirrors the inline-state pattern used by the sibling editable
 * Assignments page (`assignments/index.tsx`) and the Associate View page
 * (`associateView/index.tsx`):
 *
 *   - State is held in plain `useState` hooks (`response`, `loading`,
 *     `errorKind`, `searchQuery`) declared inline at the top of the
 *     component.
 *   - The `errorKind` field uses a small inline union literal — declared
 *     locally, not exported, no separate `state.ts` module.
 *   - The three non-content state `data-testid`s are inline string
 *     literals at the render site (no module-level testid constants),
 *     matching the sibling-page convention.
 *
 * The render ladder is mutually exclusive:
 *
 *   loading        → `RatesRosterLoader` (full-screen progress UI), wrapped
 *                    in a div with `data-testid="published-assignment-loading"`.
 *   missing-plan   → `AppModal` (error variant) whose primary action
 *                    navigates to `ROUTES.STATION_ASSIGNMENT`, wrapped in
 *                    a div with
 *                    `data-testid="published-assignment-missing-plan-id"`.
 *   generic-error  → `AppModal` (error variant) whose primary action takes
 *                    the user back to `ROUTES.STATION_ASSIGNMENT`, wrapped
 *                    in a div with
 *                    `data-testid="published-assignment-generic-error"`.
 *   success        → `<PublishedAssignmentHeader />` followed by
 *                    `<PublishedAssignmentScreen ... />`.
 *
 * URL-parameter validation happens before the API call: an `undefined`,
 * empty, or whitespace-only `:planId` short-circuits to the missing-plan
 * state without invoking `getStationAssignments`. Otherwise the
 * effect awaits `getStationAssignments(ofdDate, clockWindowName, planId)`
 * and uses the same structural-emptiness check as the wrapper's failure
 * surface (`associates.length === 0 && processPaths.length === 0`) to
 * decide whether to render success or the generic-error state. The wrapper
 * has already dispatched `showError` on a real failure, so the container
 * does not need to fire its own toast.
 *
 * The container determines the Plan_Id solely from the URL (it does not
 * read `isStationAssignmentActive` from Redux), so direct URL access works
 * for isolated development before the History screen is built.
 */
const PublishedAssignmentContainer: React.FC = () => {
  const { planId } = useParams<{ planId?: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  // The plan's shift context comes from the URL query params set by the
  // History screen when it opens a plan (see `buildStationAssignmentPublishedUrl`).
  // Fall back to today's date / the day shift for direct or deep-link access
  // where no context was supplied, so the screen still renders something sane.
  const ofdDate = searchParams.get('ofdDate') || getTodayDate();
  const shift = searchParams.get('shift') || 'day';
  const clockWindowName = getClockWindowNameBasedOnSelectedOption(shift);

  const [fullResponse, setFullResponse] =
    useState<GetStationAssignmentsResponse | null>(null);
  const [selectedHalf, setSelectedHalf] = useState<string>(
    DEFAULT_SHIFT_HALF_OPTIONS[0].value,
  );
  const [loading, setLoading] = useState<boolean>(true);
  const [errorKind, setErrorKind] = useState<
    { kind: 'generic' | 'missing-plan-id'; message?: string } | null
  >(null);
  const [searchQuery, setSearchQuery] = useState<string>('');

  useEffect(() => {
    // URL-parameter validation: an `undefined`, empty, or whitespace-only
    // `:planId` short-circuits without invoking the API. Use a synchronous
    // path so we never enter the loading state for a request we won't make.
    if (!planId || planId.trim() === '') {
      setLoading(false);
      setErrorKind({ kind: 'missing-plan-id' });
      setFullResponse(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setErrorKind(null);

    const run = async () => {
      try {
        const result = await getStationAssignments(
          ofdDate,
          clockWindowName,
          planId,
        );
        if (cancelled) return;

        // Structural emptiness check across BOTH halves: the wrapper already
        // dispatched `showError` for any real failure (returning
        // `EMPTY_RESPONSE`), and an empty-but-successful response maps to the
        // generic error state too. A plan is renderable if either half has
        // any associates or process paths.
        const firstHalf = result?.firstHalfStationAssignment;
        const secondHalf = result?.secondHalfStationAssignment;
        const halfHasData = (half?: typeof firstHalf) =>
          !!half &&
          (half.associates.length > 0 || half.processPaths.length > 0);

        if (!result || (!halfHasData(firstHalf) && !halfHasData(secondHalf))) {
          setErrorKind({ kind: 'generic' });
          setFullResponse(null);
        } else {
          setFullResponse(result);
          // Default the half selector to the first half that actually has
          // data, so a second-half-only plan lands on content instead of a
          // blank first-half board (mirrors the editable Assignments page).
          setSelectedHalf(
            halfHasData(firstHalf)
              ? 'firstHalfStationAssignment'
              : 'secondHalfStationAssignment',
          );
          setErrorKind(null);

          // Store the full response in Redux so Associate View can render
          // directly without making another API call.
          dispatch(setHistoricalPlanData(result));
          dispatch(setStationAssignmentData({ date: ofdDate, shift }));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [planId, ofdDate, clockWindowName]);

  // Flatten the currently-selected half into the read-only screen's view-model.
  // Switching the half dropdown re-derives this in place — the response already
  // carries both halves, so no re-fetch is needed.
  const response: PublishedAssignmentView | null = useMemo(() => {
    if (!fullResponse) return null;
    const activeHalf =
      fullResponse[
        selectedHalf as keyof Pick<
          GetStationAssignmentsResponse,
          'firstHalfStationAssignment' | 'secondHalfStationAssignment'
        >
      ];
    if (!activeHalf) return null;
    return {
      associates: activeHalf.associates,
      unassignedPALeadHRAssociates: activeHalf.unassignedPALeadHRAssociates,
      processPaths: activeHalf.processPaths,
      lastModifiedBy: fullResponse.lastModifiedBy,
      lastModifiedDate: fullResponse.lastModifiedDate,
    };
  }, [fullResponse, selectedHalf]);

  const handleErrorAcknowledge = () => {
    navigate(ROUTES.SHIFT_ROTATION);
  };

  // ── Render ladder ───────────────────────────────────────────────────────

  if (loading) {
    return (
      <div data-testid="published-assignment-loading">
        <RatesRosterLoader
          type="plan"
          status="loading"
          message="Loading published station assignment plan..."
        />
      </div>
    );
  }

  if (errorKind?.kind === 'missing-plan-id') {
    return (
      <div data-testid="published-assignment-missing-plan-id">
        <AppModal
          open
          title="Unable to load published assignment"
          message="A Plan_Id is required to view a published station assignment."
          variant="error"
          primaryLabel="OK"
          onPrimary={handleErrorAcknowledge}
          onClose={handleErrorAcknowledge}
        />
      </div>
    );
  }

  if (errorKind?.kind === 'generic' || !response) {
    return (
      <div data-testid="published-assignment-generic-error">
        <AppModal
          open
          title="Unable to load published assignment"
          message={
            errorKind?.message ??
            'Unable to fetch the published assignment plan details. Please try again later.'
          }
          variant="error"
          primaryLabel="OK"
          onPrimary={handleErrorAcknowledge}
          onClose={handleErrorAcknowledge}
        />
      </div>
    );
  }

  // Success branch. The header reuses `lastModifiedBy` / `lastModifiedDate`
  // as submitted-by / submitted-date (see design.md → "Plan-metadata fields
  // in the header"). The OFD-date and clock-window come from the URL query
  // params the History screen set when opening the plan (with today/day-shift
  // fallbacks for direct access), so the header reflects the actual plan.
  return (
    <Box className="published-assignment-page">
      <PublishedAssignmentHeader
        planId={planId ?? ''}
        submittedBy={response.lastModifiedBy}
        submittedDate={response.lastModifiedDate}
        ofdDate={ofdDate}
        clockWindow={clockWindowName}
        selectedHalf={selectedHalf}
        onHalfChange={setSelectedHalf}
      />
      <PublishedAssignmentScreen
        response={response}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        planId={planId ?? ''}
      />
    </Box>
  );
};

export default PublishedAssignmentContainer;

import './index.scss';